import { Checkbox, FormControlLabel, FormGroup, TextField, styled } from '@mui/material';
import { ListDescription, ListFieldset, ListLabel } from '@shared/components/core/field/list/CommonListFieldStyles';
import DeviceTypeThrottleRules, { getDefaultThrottleRules } from '@shared/models/devices/DeviceTypeThrottleRules';
import { useTranslation } from 'react-i18next';

export interface IDeviceTypeThrottleRulesEditorProps {
    throttleRules?: DeviceTypeThrottleRules;
    onUpdate: (rules: DeviceTypeThrottleRules) => void;
}

export default function DeviceTypeThrottleRulesEditor({
    throttleRules,
    onUpdate,
}: IDeviceTypeThrottleRulesEditorProps) {
    const { t } = useTranslation();

    // Initialize with default values if throttleRules is undefined
    const currentRules: DeviceTypeThrottleRules = throttleRules ?? getDefaultThrottleRules();

    const handleBooleanChange = (field: keyof DeviceTypeThrottleRules, value: boolean) => {
        onUpdate({
            ...currentRules,
            [field]: value,
        });
    };

    const handleNumberChange = (field: keyof DeviceTypeThrottleRules, value: string) => {
        if (value === '') {
            onUpdate({
                ...currentRules,
                [field]: undefined,
            });
            return;
        }
        const numValue = parseInt(value, 10);
        if (!isNaN(numValue) && numValue >= 0) {
            onUpdate({
                ...currentRules,
                [field]: numValue,
            });
        }
    };

    return (
        <ListFieldset>
            <ListLabel as="legend">{t('common:organization.projects.deviceType.throttleRules.title')}</ListLabel>
            <ListDescription>{t('common:organization.projects.deviceType.throttleRules.description')}</ListDescription>

            <StyledFormGroup>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={currentRules.disableVoiceInput}
                            onChange={(e) => handleBooleanChange('disableVoiceInput', e.target.checked)}
                        />
                    }
                    label={t('common:organization.projects.deviceType.throttleRules.disableVoiceInput')}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={currentRules.disableHotkeyInput}
                            onChange={(e) => handleBooleanChange('disableHotkeyInput', e.target.checked)}
                        />
                    }
                    label={t('common:organization.projects.deviceType.throttleRules.disableHotkeyInput')}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={currentRules.disableCameraInput}
                            onChange={(e) => handleBooleanChange('disableCameraInput', e.target.checked)}
                        />
                    }
                    label={t('common:organization.projects.deviceType.throttleRules.disableCameraInput')}
                />
            </StyledFormGroup>

            <NumberInputsContainer>
                <TextField
                    type="number"
                    label={t('common:organization.projects.deviceType.throttleRules.maximumCommandsPerSecond')}
                    value={currentRules.maximumCommandsPerSecond ?? ''}
                    onChange={(e) => handleNumberChange('maximumCommandsPerSecond', e.target.value)}
                    inputProps={{ min: 0 }}
                    fullWidth
                />
                <TextField
                    type="number"
                    label={t('common:organization.projects.deviceType.throttleRules.maximumCommandsPerMinute')}
                    value={currentRules.maximumCommandsPerMinute ?? ''}
                    onChange={(e) => handleNumberChange('maximumCommandsPerMinute', e.target.value)}
                    inputProps={{ min: 0 }}
                    fullWidth
                />
                <TextField
                    type="number"
                    label={t('common:organization.projects.deviceType.throttleRules.maximumMacroLength')}
                    value={currentRules.maximumMacroLength ?? ''}
                    onChange={(e) => handleNumberChange('maximumMacroLength', e.target.value)}
                    inputProps={{ min: 0 }}
                    fullWidth
                />
            </NumberInputsContainer>
        </ListFieldset>
    );
}

const StyledFormGroup = styled(FormGroup)`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    width: 100%;
    margin-bottom: 16px;
`;

const NumberInputsContainer = styled('div')`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 16px;
    width: 100%;
`;
